import React from 'react';
import { useTranslation } from 'react-i18next';
import './Pagination.css';

const Pagination = React.memo(({
    pagination,
    onPageChange,
    onLimitChange,
    showLimitSelector = true,
    limitOptions = [10, 20, 50, 100] // Default fallback
}) => {
    const { t } = useTranslation(['common']);
    
    if (!pagination) return null;

    const { page, limit, total, totalPages, hasNext, hasPrev } = pagination;

    const generatePageNumbers = () => {
        const pages = [];
        const maxVisiblePages = 5;
        
        if (totalPages <= maxVisiblePages) {
            // Show all pages if total is small
            for (let i = 1; i <= totalPages; i++) {
                pages.push(i);
            }
        } else {
            // Show smart pagination
            const half = Math.floor(maxVisiblePages / 2);
            let start = Math.max(1, page - half);
            let end = Math.min(totalPages, start + maxVisiblePages - 1);
            
            // Adjust start if we're near the end
            if (end - start < maxVisiblePages - 1) {
                start = Math.max(1, end - maxVisiblePages + 1);
            }
            
            // Add first page and ellipsis if needed
            if (start > 1) {
                pages.push(1);
                if (start > 2) {
                    pages.push('...');
                }
            }
            
            // Add visible pages
            for (let i = start; i <= end; i++) {
                pages.push(i);
            }
            
            // Add ellipsis and last page if needed
            if (end < totalPages) {
                if (end < totalPages - 1) {
                    pages.push('...');
                }
                pages.push(totalPages);
            }
        }
        
        return pages;
    };

    const handlePageClick = (newPage) => {
        if (newPage !== page && newPage >= 1 && newPage <= totalPages) {
            onPageChange(newPage);
        }
    };

    const handleLimitChange = (e) => {
        const newLimit = parseInt(e.target.value);
        if (onLimitChange) {
            onLimitChange(newLimit);
        }
    };

    const startItem = (page - 1) * limit + 1;
    const endItem = Math.min(page * limit, total);

    return (
        <div className="pagination-container">
            <div className="pagination-info">
                <span>
                    {t('common:showing')} {startItem}-{endItem} {t('common:of')} {total} {t('common:items')}
                </span>
                
                {showLimitSelector && (
                    <div className="items-per-page">
                        <label>
                            {t('common:itemsPerPage')}:
                            <select 
                                value={limit} 
                                onChange={handleLimitChange}
                                className="limit-selector"
                            >
                                {limitOptions.map(option => (
                                    <option key={option} value={option}>
                                        {option}
                                    </option>
                                ))}
                            </select>
                        </label>
                    </div>
                )}
            </div>

            <div className="pagination-controls">
                {/* First page button */}
                <button
                    onClick={() => handlePageClick(1)}
                    disabled={!hasPrev}
                    className="pagination-btn"
                    aria-label={t('common:firstPage')}
                >
                    ‹‹
                </button>

                {/* Previous page button */}
                <button
                    onClick={() => handlePageClick(page - 1)}
                    disabled={!hasPrev}
                    className="pagination-btn"
                    aria-label={t('common:previousPage')}
                >
                    ‹ {t('common:previous')}
                </button>

                {/* Page numbers */}
                <div className="page-numbers">
                    {generatePageNumbers().map((pageNum, index) => (
                        pageNum === '...' ? (
                            <span key={`ellipsis-${index}`} className="pagination-ellipsis">
                                ...
                            </span>
                        ) : (
                            <button
                                key={pageNum}
                                onClick={() => handlePageClick(pageNum)}
                                className={`pagination-btn page-number ${pageNum === page ? 'active' : ''}`}
                                aria-label={`${t('common:page')} ${pageNum}`}
                            >
                                {pageNum}
                            </button>
                        )
                    ))}
                </div>

                {/* Next page button */}
                <button
                    onClick={() => handlePageClick(page + 1)}
                    disabled={!hasNext}
                    className="pagination-btn"
                    aria-label={t('common:nextPage')}
                >
                    {t('common:next')} →
                </button>

                {/* Last page button */}
                <button
                    onClick={() => handlePageClick(totalPages)}
                    disabled={!hasNext}
                    className="pagination-btn"
                    aria-label={t('common:lastPage')}
                >
                    ››
                </button>
            </div>
        </div>
    );
});

export default Pagination;